import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { MarinaOrder, OrderItem, Sling, SlingConfig } from 'src/app/models';
import { BoatStatus, SlingStatus } from 'src/app/models/enums';
import { OrderStatus } from 'src/app/models/order-status';
import { ProductCategoryType } from 'src/app/models/product-category-type';
import { SlingCheckComponent } from 'src/app/pages/operational/sling-check/sling-check.component';
import { MarinaOrderService } from 'src/app/services/marina-order.service';
import { OrderItemService } from 'src/app/services/order-item.service';
import { SlingService } from 'src/app/services/sling.service';
import { ToastService } from 'src/app/services/toast.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { OrderUtil } from 'src/app/utils/order.util';
import { SlingUtil } from 'src/app/utils/sling.util';
import moment from 'moment';
import Swal from 'sweetalert2';
import { SlingConfirmOperationComponent } from '../sling-confirm-operation/sling-confirm-operation.component';
import { SlingOperationComponent } from '../sling-operation/sling-operation.component';
import { SlingOrderDetailsComponent } from '../sling-order-details/sling-order-details.component';
import { SlingOwnFuelConfirmComponent } from '../sling-own-fuel-confirm/sling-own-fuel-confirm.component';
import { SlingFuelCheckComponent } from '../sling-fuel-check/sling-fuel-check.component';
import { SlingConsumableCheckComponent } from '../sling-consumable-check/sling-consumable-check.component';
import { SlingAccessoryCheckComponent } from '../sling-accessory-check/sling-accessory-check.component';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { Subscription } from 'rxjs';
import { WebSocketService } from 'src/app/services/web-socket.service';
import { OptionPrintQuestionTotem } from 'src/app/models/enums/option-print-question-totem';
import { PrintingService } from 'src/app/services/printing-service';
import { PrintType } from 'src/app/models/enums/print-type';

@Component({
  selector: 'app-sling-down-tab',
  templateUrl: './sling-down-tab.component.html',
  styleUrls: ['./sling-down-tab.component.scss']
})
export class SlingDownTabComponent implements OnInit {

  @Input()
  isMobile = false;

  @Input()
  isTablet = false;

  @Input()
  slingConfig: SlingConfig;

  @Input()
  slingQuey:number;

  slingsCheck: Sling[] = [];

  referenceTime: any = new Date();

  slingCheckComponent: SlingCheckComponent;
  orders: any[];

  slingSubscription: Subscription;
  broadcastSubscription$: any;

  constructor(
    private slingService: SlingService,
    private messageUtil: MessageUtil,
    public dialog: DialogService,
    public orderUtil: OrderUtil,
    private router: Router,
    private marinaOrderService: MarinaOrderService,
    private spinner: NgxSpinnerService,
    private orderItemService: OrderItemService,
    private toasty: ToastService,
    public slingUtil: SlingUtil,
    private webSocketService: WebSocketService,
    private printingService: PrintingService
  ) { }

  async ngOnInit(): Promise<void> {
    this.broadcastSubscription$ = BroadcastService.get('reloadSlings').subscribe(() => this.fillSlingTable());
    this.spinner.show();
    await this.fillSlingTable();
    this.spinner.hide();
    this.slingSubscription = this.webSocketService.getMessageSling().subscribe(async (message) => {
      this.spinner.show();
      await this.fillSlingTable();
      this.spinner.hide();
    });
  }

  ngOnDestroy(): void {
    this.webSocketService.unsubscribeSling(this.slingSubscription);
    this.broadcastSubscription$.unsubscribe();
  }

  async fillSlingTable(): Promise<void> {
    this.slingsCheck = new Array();
    const referenceTime = new Date(this.referenceTime);
    if (this.slingConfig.slingsNextDay) {
      referenceTime.setDate(referenceTime.getDate() + 1);
    }
    return new Promise<void>(
      async (resolve) => {

        this.slingService.getSlingQueueV2(referenceTime.getTime()).subscribe(
          async (data) => {
            this.slingsCheck = data.sort((a, b) => {
              return a.executionForecast - b.executionForecast;
            });
            if (this.slingQuey!==null && this.slingQuey!==undefined && this.slingQuey>0) {
              this.slingsCheck = this.slingsCheck.filter(x => x.slingConfigMultQueue.id == this.slingQuey);
            }
            if (this.slingConfig.finishOnWater) {
              await this.findBoatIdsFinishedOnWater();
            }
            resolve();
          });
      }
    );
  }

  async findBoatIdsFinishedOnWater(): Promise<void> {
    return new Promise<void>(
      async (resolve) => {
        this.slingService.findBoatIdsWithSlingFinishedOnWater().subscribe(async (boatIds: number[]) => {
          // tslint:disable-next-line: prefer-for-of
          for (let index = 0; index < boatIds.length; index++) {
            const id = boatIds[index];
            const slings = this.slingsCheck.filter((x) => x.boat.id === id);
            if (slings.length > 0) {
              // tslint:disable-next-line: prefer-for-of
              for (let idx = 0; idx < slings.length; idx++) {
                const sling = slings[idx];
                sling.hasSlingFinishedOnWater = true;
              }
            }
          }
          resolve();
        });
      }
    );
  }

  hasFuel(sling: Sling): boolean {
    const fuels = sling.marinaOrder.orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType === ProductCategoryType.Fuel
    );
    return (sling.marinaOrder.orderStatus !== OrderStatus.Canceled && fuels.length > 0);
  }

  hasConsumables(sling: Sling): boolean {
    const consumables = sling.marinaOrder.orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType !== ProductCategoryType.Fuel
    );
    return sling.marinaOrder.orderStatus !== OrderStatus.Canceled && consumables.length > 0;
  }

  hasAcessories(sling: Sling): boolean {
    return sling.marinaOrder.orderStatus !== OrderStatus.Canceled && sling.slingAccessories.length > 0;
  }

  getFuel(orderItems: OrderItem[]): OrderItem {
    const fuels = orderItems.filter((orderItem) => orderItem.product.productCategory.productCategoryType === ProductCategoryType.Fuel);
    if (fuels.length > 0) {
      return fuels[0];
    }
    return new OrderItem();
  }

  countConsumables(orderItems: OrderItem[]): number {
    const consumables = orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType !== ProductCategoryType.Fuel
    );
    return consumables.length;
  }

  checkPendingFuel(orderItems: OrderItem[]): boolean {
    const fuels = orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType === ProductCategoryType.Fuel && !orderItem.itemStatus
    );
    return fuels.length > 0;
  }

  checkPendingConsumables(orderItems: OrderItem[]): boolean {
    const consumables = orderItems.filter(
      (orderItem) => orderItem.product.productCategory.productCategoryType !== ProductCategoryType.Fuel && !orderItem.itemStatus
    );
    return consumables.length > 0;
  }

  checkPendingAcessories(sling: Sling): boolean {
    const acessories = sling.slingAccessories.filter((x) => !x.attended);
    return acessories.length > 0;
  }

  isReadyToSling(sling: Sling): boolean {
    const orderItems: OrderItem[] = sling.marinaOrder.orderItems;
    return (
      sling.marinaOrder.orderStatus === OrderStatus.Canceled ||
      !this.checkPendingConsumables(orderItems) &&
      !this.checkPendingFuel(orderItems) &&
      sling.slingAccessories.find((sa) => sa.attended == null) === undefined &&
      sling.prepare &&
      !sling.ownFuelSupply
    );
  }

  hasPendingItems(sling: Sling): boolean {
    return (
      this.checkPendingFuel(sling.marinaOrder.orderItems) ||
      this.checkPendingConsumables(sling.marinaOrder.orderItems) ||
      sling.ownFuelSupply
    );
  }

  hasBoatSailor(sling: Sling): boolean {
    return !!sling.boatSailor;
  }

  getImageSailorResponsable() {
    return '/assets/images/sailorOnSlingBlack.png';
  }

  hasSailorConfig(): boolean {
    return this.slingConfig.hasSailor;
  }

  updateSling(sling: Sling, boatStatus: BoatStatus): void {
    // EAS-245: ao descer o PRINCIPAL de um grupo (marina com agendamento múltiplo), pergunta se quer
    // descer também as filhas que estão no MESMO status (as que já desceram ficam de fora).
    if (this.slingConfig.allowAdditionalBoats && sling.groupPrincipal && sling.slingGroupId) {
      this.slingService.findGroupSiblings(sling.slingGroupId).subscribe(
        (siblings) => {
          const pendentes = (siblings || []).filter(
            (s) => s.id !== sling.id && s.slingStatus === sling.slingStatus);
          if (pendentes.length > 0) {
            const nomes = pendentes.map((s) => s.boatName).join(', ');
            Swal.fire({
              title: 'Descer embarcações do grupo?',
              text: `Descer também junto: ${nomes}?`,
              icon: 'question',
              showCancelButton: true,
              showDenyButton: true,
              confirmButtonText: 'Descer todas',
              denyButtonText: 'Só esta',
              cancelButtonText: 'Cancelar',
              reverseButtons: true,
            }).then((result) => {
              if (result.isConfirmed) {
                this.downGroup(sling, boatStatus);
              } else if (result.isDenied) {
                this.doUpdateSling(sling, boatStatus);
              }
            });
          } else {
            this.doUpdateSling(sling, boatStatus);
          }
        },
        () => this.doUpdateSling(sling, boatStatus)
      );
    } else {
      this.doUpdateSling(sling, boatStatus);
    }
  }

  private doUpdateSling(sling: Sling, boatStatus: BoatStatus): void {
    sling.slingStatus = this.slingConfig.queueControl ? SlingStatus.WATER : SlingStatus.PENDING_UP;
    sling.slingDownAt = moment().format('YYYY-MM-DDTHH:mm');
    sling.boatStatus = boatStatus;
    this.slingService.updateOnly(sling).subscribe(
      () => {
        this.toasty.success(this.messageUtil.translateKey('MSG.SLING-SUCCESS'));
        this.fillSlingTable();
      },
      (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      });
  }

  // EAS-245: desce o principal + as irmãs no mesmo status, em lote (backend downGroup).
  private downGroup(sling: Sling, boatStatus: BoatStatus): void {
    this.slingService.downGroup(sling.id, boatStatus).subscribe(
      () => {
        this.toasty.success(this.messageUtil.translateKey('MSG.SLING-SUCCESS'));
        this.fillSlingTable();
      },
      (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      });
  }

  confirmationNotAttemptedItens(sling: Sling): void {
    const isReady = this.isReadyToSling(sling);
    if (isReady) {
      if (this.slingConfig.manageDeck) {
        this.mooredConfirmModal(sling);
      } else {
        this.openDialogSlingDown(sling);
      }
    } else {
      Swal.fire({
        title: 'Alguns itens não foram atendidos. Deseja continuar?',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Confirmar',
        reverseButtons: true,
      }).then((result) => {
        if (result.value) {
          if (this.slingConfig.manageDeck) {
            this.mooredConfirmModal(sling);
          } else {
            this.openDialogSlingDown(sling);
          }
        }
      });
    }
  }

  mooredConfirmModal(sling: Sling): void {
    const config: DynamicDialogConfig = {
      header: 'Confirmação - ' + sling.boat.name + ' - ' + sling.navigationPlan.responsible.name + '',
      data: {
        sling,
        origin: 'SlingCheckDown',
        manageDeck: this.slingConfig.manageDeck
      }
    };
    if (this.isMobile) {
      config.width = '90%';
    }
    this.dialog.open(SlingOperationComponent, config).onClose.subscribe((result: any) => {
      if (result.confirm) {
        this.updateSling(sling, result.boatStatus);
      }
    });
  }

  openDialogSlingDown(sling: Sling): void {
    Swal.fire({
      title: 'Confirmar operação?',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      showLoaderOnConfirm: true,
      reverseButtons: true,
      cancelButtonColor: '#d33',
      allowOutsideClick: false,
      preConfirm: () => {
        this.updateSling(sling, BoatStatus.SAILING);
      }
    });
  }

  prepareConfirmation(sling: Sling): void {
    if (this.slingConfig.slingDownOnPreparation) {
      this.verifyPreparation(sling);
    } else {
      sling.boatStatus = BoatStatus.PREPARED;
      sling.slingStatus = SlingStatus.PENDING_DOWN;
      this.confirmOperation(sling);
    }
  }

  private verifyPreparation(sling: Sling): void {
    if (this.slingConfig.queueControl && this.slingConfig.manageDeck) {
      sling.prepare = true;
      this.mooredConfirmModal(sling);
    } else if (this.slingConfig.queueControl) {
      sling.boatStatus = BoatStatus.SAILING;
      sling.slingStatus = SlingStatus.WATER;
      this.confirmOperation(sling);
    } else {
      sling.boatStatus = BoatStatus.SAILING;
      sling.slingStatus = SlingStatus.PENDING_UP;
      this.confirmOperation(sling);
    }
  }

  private confirmOperation(sling: Sling): void {
    Swal.fire({
      title: 'Confirmar operação?',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      showLoaderOnConfirm: true,
      reverseButtons: true,
      cancelButtonColor: '#d33',
      allowOutsideClick: false,
      preConfirm: () => {
        this.prepare(sling);
      }
    });
  }

  prepare(sling): void {
    this.slingService.prepare(sling).subscribe(
      () => {
        this.toasty.success('Lingada preparada com sucesso');
        this.fillSlingTable();
      },
      (error) => {
        const exception = error.error.data.exception;
        this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
      }
    );
  }

  toConfirmation(sling): void {
    const config: DynamicDialogConfig = {
      header: 'Confirmar lingada - ' + sling.boat.name,
      data: {
        sling
      }
    };
    if (this.isMobile) {
      config.width = '90%';
    }
    this.dialog.open(SlingConfirmOperationComponent, config).
      onClose.subscribe(() => {
        setTimeout(() => {
          this.fillSlingTable();
        }, 1500);
      });
  }

  isNotToday(slingCheck: Sling): boolean {
    if (new Date(slingCheck.executionForecast).setHours(0, 0, 0, 0) !== this.referenceTime.setHours(0, 0, 0, 0)) {
      return true;
    }
    return false;
  }

  isNumber(value: any): boolean {
    return typeof value === 'number';
  }

  openSingleRequest(slingCheck): void {
    if (this.orders.length > 0) {
      const config: DynamicDialogConfig = {
        header: 'Embarcação: ' + slingCheck.boat.name + ' - ' + moment().format('DD/MM/YY'),
        data: {
          sling: slingCheck,
          customers: slingCheck.boat.boatCustomers.map((bc) => bc.customer),
          orders: this.orders
        },
        width: '70%'
      };
      if (this.isMobile || this.isTablet) {
        config.width = '90%';
      }
      this.dialog.open(SlingOrderDetailsComponent, config);
    } else {
      this.router.navigate(
        ['/single-order'],
        {
          queryParams:
          {
            customerId: slingCheck.navigationPlan.responsible.id,
            boatId: slingCheck.boat.id,
            slingId: slingCheck.id
          }
        }
      );
    }
  }

  loadMarinaOrders(slingCheck): void {
    this.spinner.show();
    this.marinaOrderService.getDebitsCustomersToDate(new Date().getTime(),
      slingCheck.boat.boatCustomers
        .map((bc) => bc.customer)
        .map((c) => c.id).join()).subscribe(
          (data) => {
            this.orders = data;
            this.spinner.hide();
            this.openSingleRequest(slingCheck);
          },
          (error) => {
            this.spinner.hide();
            const exception = error.error.data.exception;
            this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
          }
        );
  }

  confirmOwnFuelSupply(sling: Sling): void {
    this.dialog.open(SlingOwnFuelConfirmComponent, {
      width: '576px',
      header: 'Confirmar abastecimento próprio da embarcação ' + sling.boat.name + '?',
      data: {
        sling
      }
    }).onClose.subscribe(() => {
      setTimeout(() => {
        this.fillSlingTable();
      }, 1500);
    });
  }

  openDialogFuelOrder(sling: Sling): void {
    if (sling.prepare) {
      const fuel = sling.marinaOrder.orderItems.filter(
        (x) => x.product.productCategory.productCategoryType === ProductCategoryType.Fuel
      )[0];
      this.dialog.open(SlingFuelCheckComponent, {
        header: this.messageUtil.translateKey('FUEL'),
        data: {
          slingId: sling.id,
          fuelOrder: fuel,
          referenceTime: this.referenceTime,
          fullTankCheck: false
        }
      }).onClose.subscribe((result) => {
        if (result !== null && result !== undefined) {
          console.log(result)
          Swal.fire({
            title: 'Confirmar operação?',
            showCancelButton: true,
            confirmButtonText: 'Confirmar',
            showLoaderOnConfirm: true,
            reverseButtons: true,
            cancelButtonColor: '#d33',
            allowOutsideClick: false,
            preConfirm: () => {
              fuel.itemStatus = true;
              fuel.qtySupplied = result;
              fuel.dateSupply = moment().format('YYYY-MM-DDTHH:mm:ss');
              fuel.itemValue = fuel.product.price.value;
              fuel.marinaOrder = sling.marinaOrder;
              fuel.marinaOrder.sling = null;
              fuel.marinaOrder.orderItems = [];
              this.updateOrderItem(new Array(fuel), ProductCategoryType.Fuel);
            }
          });
        }
      });
    } else {
      this.messageUtil.generateMessage('warning', 'Aguardando preparo', 'Inicie o preparo para fornecer combustível.');
    }
  }

  openDialogConsumableOrder(order: Sling): void {
    if (order.prepare) {
      const dialogRef = this.dialog.open(SlingConsumableCheckComponent, {
        header: this.messageUtil.translateKey('CONSUMABLES'),
        data: {
          slingId: order.id,
          referenceTime: this.referenceTime
        }
      });

      dialogRef.onClose.subscribe((result) => {
        if (result) {
          const consumables: OrderItem[] = new Array();
          this.validItems(result, result.marinaOrder.orderItems, consumables);
        } else {
          this.fillSlingTable();
        }
      });
    } else {
      this.messageUtil.generateMessage('warning', 'Aguardando preparo', 'Inicie o preparo para fornecer consumíveis.');
    }
  }

  validItems(slingOrder, orderItems: OrderItem[], consumables: OrderItem[]): boolean {
    let typeErrorQty = '';
    for (const consumable of orderItems) {
      if (consumable.qtySupplied == null) {
        this.messageUtil.generateMessage('error', 'SUMMARY.ERROR', 'Informar a quantidade');
        return false;
      }
      if (consumable.qtySupplied > consumable.qtyRequested) {
        typeErrorQty = 'more';
      } else if (consumable.qtySupplied < consumable.qtyRequested) {
        typeErrorQty = 'less';
      }
      consumable.itemStatus = true;
      if (!consumable.product.price.priceFree) {
        consumable.itemValue = consumable.product.price.value;
      }
      consumable.marinaOrder = new MarinaOrder();
      consumable.marinaOrder.id = slingOrder.marinaOrder.id;
      consumables.push(consumable);
    }
    if (typeErrorQty === '') {
      this.saveConsumables(consumables);
    } else {
      this.confirmDifferentConsumable(typeErrorQty, consumables);
    }
  }

  confirmDifferentConsumable(typeErrorQty, consumables): void {
    const titleMsg = 'Quantidade diferente';
    let textMsg = '';
    if (typeErrorQty === 'more') {
      textMsg = 'A quantidade fornecida é superior a solicitada. Deseja continuar?';
    } else {
      textMsg = 'A quantidade fornecida é inferior a solicitada. Deseja continuar?';
    }
    Swal.fire({
      title: titleMsg,
      text: textMsg,
      icon: 'warning',
      reverseButtons: true,
      showCancelButton: true,
      cancelButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Confirmar'
    }).then((response) => {
      if (response.value) {
        this.saveConsumables(consumables);
      }
    });
  }

  saveConsumables(consumables: OrderItem[]): void {
    Swal.fire({
      title: 'Confirmar operação?',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      showLoaderOnConfirm: true,
      reverseButtons: true,
      cancelButtonColor: '#d33',
      allowOutsideClick: false,
      preConfirm: () => {
        consumables.forEach((c: OrderItem) => {
          c.dateSupply = moment().format('YYYY-MM-DDTHH:mm:ss');
        });
        this.updateOrderItem(consumables, ProductCategoryType.Consumable);
      }
    });
  }

  openDialogSlingAccessories(sling: Sling): void {
    if (sling.prepare) {
      this.dialog.open(SlingAccessoryCheckComponent, {
        header: this.messageUtil.translateKey('ACCESSORIES'),
        data: {
          slingId: sling.id,
          referenceTime: this.referenceTime
        }
      }).onClose.subscribe(() => {
        this.fillSlingTable();
      });
    } else {
      this.messageUtil.generateMessage('warning', 'Aguardando preparo', 'Inicie o preparo para fornecer acessórios.');
    }
  }

  updateOrderItem(orderItems: OrderItem[], category: ProductCategoryType): void {
    this.orderItemService.saveChecking(orderItems).subscribe({
      next: (order) => {
        switch (category) {
          case ProductCategoryType.Consumable:
            this.toasty.success('Produto fornecido com sucesso');
            break;
          case ProductCategoryType.Service:
            this.toasty.success('Serviço fornecido com sucesso');
            break;
          case ProductCategoryType.Fuel:
            this.toasty.success('Combustível fornecido com sucesso');
            break;
          default:
            this.toasty.success('Acessório fornecido com sucesso');
            break;
        }
        if (this.slingConfig.printNavigationPlanWeb == OptionPrintQuestionTotem.Question) {
          Swal.fire({
            title: 'Atenção',
            text: 'Deseja imprimir a Comanda?',
            icon: 'warning',
            reverseButtons: true,
            showDenyButton: true,
            customClass: {
              confirmButton: ['new-easymarine', 'primary'],
              denyButton: ['new-easymarine', 'disabled']
            },
            confirmButtonText: 'SIM',
            denyButtonText: 'NÃO',
          }).then(async (result) => {
            if (result.isConfirmed) {
              await this.printOrderReport(order.id);
            }
            if (result.isDenied || result.isDismissed) {
            }
          });
        }
        if (this.slingConfig.printNavigationPlanWeb == OptionPrintQuestionTotem.AutoPrint) {
          this.printOrderReport(order.id);
        }
        this.fillSlingTable();
      },
      error: (error) => {
        this.messageUtil.generateMessage('error', 'SUMMARY.ERROR', error.error.message);
        this.fillSlingTable();
      }
    });
  }

  printOrderReport(id: number): void {
    this.spinner.show();
    const idsSling: number[] = [id];
    this.printingService.generatePdfById(id, PrintType.ORDER_CHECK).subscribe((data) => {
      this.spinner.hide();
      const url = window.URL.createObjectURL(data);
      window.open(url);
    });
  }
}
